import * as React from 'react';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FileBrowser } from '@/components/files';
import { InfoPanel } from '@/components/sidebar';
import type { UseWs } from '@/hooks/use-ws';
import { usePaneStore } from '@/stores/pane';
import {
  LazyAdminPane,
  LazyBrowserPane,
  LazyCronApprovalsPane,
  LazyLoopsPane,
  LazyOfficePane,
  LazyExtrasPane,
  LazyGitPane,
  LazyModelsPane,
  LazyObservabilityPane,
  LazyPluginsPane,
  LazyProvidersPane,
  LazySettingsPane,
  LazySetupPane,
  LazyTerminalPane,
  LazyTestingPane,
  LazyTodosPane,
  LazyUsagePane,
  PaneFallback,
} from '@/components/panes/lazy-panes';

/**
 * InspectorPanel — right-sidebar tabs. Info stays the default; Providers is
 * the real W2-5 pane (live CRUD + connection test), Models the real W2-6
 * pane (enable/disable over `PATCH /api/models`), Usage the real W2-7
 * pane (token/cost accounting over `GET /api/usage/*` + CSV/JSONL export),
 * Settings the real W2-8 pane (Config/Appearance/Permissions/MCP over
 * `GET/PATCH /api/config`), Terminal the real W3-10 pane (live shells over
 * `POST /api/terminal` + WS `terminal/*` frames), Git the real W3-11 pane
 * (branch/status/log/commit/push over `GET/POST /api/git/*` + live locks),
 * Browser the real W3-12 pane (per-agent live tabs + server-owned history
 * over `GET/POST /api/browser/*`, pages in a sandboxed iframe — REQ-109:
 * the live surface lives in tiling pane tabs and the mobile inline view,
 * desktop sidebars show only a pane redirect card), Agents the
 * real W4-13 pane (registry CRUD + pause/resume/kill/fork/clone +
 * SOUL.md/MEMORY.md editors over `GET/POST/PATCH/DELETE /api/agents/*`),
 * Vault the real W4-15 pane (live file graph +
 * note reader with `[[wikilink]]` navigation + ingest over
 * `GET /api/vault/graph|tree`, `GET /api/vault/note`,
 * `POST /api/vault/ingest`), Skills the real W4-16 pane (live registry
 * over `GET /api/skills`, skill_view preview over `GET /api/skills/:id`,
 * reference loads over `GET /api/skills/:id/file`, curator patches over
 * `POST /api/skills/:id`, telemetry over `.usage.json`), Archify the real
 * W5-17 pane (typed IR → validated HTML/SVG over `GET/POST /api/archify/*`,
 * viewer + receipt + Before/Delta/After + real file exports), Design the
 * real W5-18 pane (6 artifact types over bundled systems + a real
 * `.lokma/DESIGN.md` guard over `GET/POST/PUT /api/design/*`, sandboxed
 * viewer + Code/Critique/Export tabs + real file downloads), Testing the
 * real W5-19 pane (Plan→Run→Classify→Report over live handlers +
 * Shannon scan over `GET/POST /api/tests/*`, per-test rows + real
 * `junit.xml` download), Bots lives in its own mode since REQ-161 (the gallery over
 * `GET /api/bots` + create/fork/publish/run over `POST/PATCH`
 * `/api/bots/*`, playground runs spawn a real agent + session), Auth the
 * real W6-21 pane (login + RBAC matrix + projects + members over
 * `POST/GET/PATCH /api/auth/*`, `GET/POST/PATCH/DELETE /api/users/*`,
 * `GET/POST/PATCH/DELETE /api/projects/*`, invite accept + copyable
 * link, viewer-403 gates enforced server-side), Setup the real W6-22
 * pane (`lokma init` + optional-stack flags + `lokma doctor` probes over
 * `GET/POST /api/setup*` + `GET /api/doctor`), Plugins the real W6-23
 * pane (kernel registry + hot toggle + add-from-URL over
 * `GET/PATCH /api/plugins/*` + `POST/DELETE`, suspended routes answer
 * 503 with no restart), Observability the real W6-24 pane (agent trace
 * timeline from durable state + session replay from JSONL + frozen share
 * snapshots over `GET /api/agents/:id/trace` + `GET/POST/DELETE`
 * `/api/share/*`). Cron the real W6-25 pane (per-agent cron CRUD over
 * `GET/POST/PATCH/DELETE /api/agents/:id/cron` + `GET /api/cron`, approvals
 * rules over the shared `PATCH /api/config` permissions store, WS decision
 * history over `GET /api/approvals`). Extras the real W6-26 pane (23 ranked
 * agent-system extras as a live feature-flag board over `GET/PATCH
 * /api/config` `features`, shipped rows opening their real Inspector tab).
 * Memory the real memory-deep wave 2 pane (global MEMORY.md / USER.md
 * entries + live usage meter over `GET/POST/PATCH/DELETE /api/memory`).
 * Agent Hub (REQ-163), Orchestration (REQ-164), Vault + Memory
 * (REQ-165) and Skills (REQ-166) moved OUT of the tabs: their rail icons
 * open the Settings modal's sections instead. Archify (REQ-167) left the
 * tabs too — its rail icon opens its own standalone modal.
 * Later waves add tabs here; the W7 pane system may relocate
 * the whole panel without touching the panes themselves.
 *
 * REQ-008 — `requestedTab` lets the activity rail open a tab from outside:
 * when the prop changes to a non-null tab the panel switches to it (also
 * fires on mount, so a rail click that first reveals the sidebar still
 * lands on the right tab).
 *
 * REQ-020 — navigation lives in the InspectorRail (REQ-010); this panel
 * renders only the selected content, no tab list.
 */
export type InspectorTab =
  // REQ-043 — Files is its own Inspector page (rail icon, VS Code
  // Explorer position): only the FileBrowser, no sessions/server card.
  | 'files'
  | 'info'
  | 'providers'
  | 'models'
  | 'usage'
  | 'settings'
  | 'terminal'
  | 'git'
  | 'browser'
  | 'testing'
  | 'setup'
  | 'plugins'
  | 'observability'
  | 'cron'
  // REQ-202 — the Loops console as an Inspector tab.
  | 'loops'
  // REQ-204 — the Office pane as an Inspector tab (slice 2).
  | 'office'
  | 'extras'
  | 'todos';

export function InspectorPanel({
  onOpenSession,
  sessionId,
  ws,
  requestedTab,
  browserMode = 'pane-redirect',
}: {
  onOpenSession?: (id: string) => void;
  sessionId?: string;
  ws?: UseWs;
  requestedTab?: InspectorTab | null;
  /**
   * REQ-109 — where the browser surface lives. Desktop sidebars use the
   * default 'pane-redirect' (a card pointing at the tiling pane, never the
   * live browser); the mobile single-view has no panes (REQ-024) so it
   * passes 'inline' and keeps the real browser here.
   */
  browserMode?: 'pane-redirect' | 'inline';
}) {
  const [tab, setTab] = React.useState<InspectorTab>('files');

  React.useEffect(() => {
    if (requestedTab) setTab(requestedTab);
  }, [requestedTab]);

  return (
    <div className="space-y-3">
      <React.Suspense fallback={<PaneFallback pane={tab} />}>
      {tab === 'info' ? (
        <InfoPanel />
      ) : tab === 'files' ? (
        // REQ-043 — the Files page shows ONLY files (same session scope
        // the docked browser used to have, now behind its own tab).
        sessionId ? (
          <FileBrowser key={sessionId} sessionId={sessionId} />
        ) : (
          <div className="rounded border border-dashed p-3 text-xs text-muted-foreground">
            Open a session to browse files.
          </div>
        )
      ) : tab === 'todos' ? (
        <LazyTodosPane sessionId={sessionId} onOpenSession={onOpenSession} />
      ) : tab === 'extras' ? (
        <LazyExtrasPane onOpenTab={(t) => setTab(t)} />
      ) : tab === 'cron' ? (
        <LazyCronApprovalsPane />
      ) : tab === 'loops' ? (
        // REQ-202 — the Loops console, same component as the pane tab and
        // the Settings section (one implementation, three hosts).
        <LazyLoopsPane />
      ) : tab === 'office' ? (
        // REQ-204 — the Office pane, same component as the tiling tab.
        <LazyOfficePane />
      ) : tab === 'observability' ? (
        <LazyObservabilityPane />
      ) : tab === 'plugins' ? (
        <LazyPluginsPane />
      ) : tab === 'setup' ? (
        <LazySetupPane />
      ) : tab === 'testing' ? (
        <LazyTestingPane />
      ) : tab === 'providers' ? (
        <LazyProvidersPane />
      ) : tab === 'models' ? (
        <LazyModelsPane />
      ) : tab === 'usage' ? (
        <LazyUsagePane onOpenSession={onOpenSession} />
      ) : tab === 'settings' ? (
        <LazySettingsPane />
      ) : tab === 'git' ? (
        <LazyGitPane key={sessionId ?? 'no-session'} sessionId={sessionId} />
      ) : tab === 'browser' ? (
        // REQ-109 — the desktop sidebar never renders the browser surface;
        // a stale 'browser' state (deep-link, Extras Open button) lands on
        // the redirect card with a one-click path to the pane. Mobile
        // single-view (browserMode 'inline') keeps the real browser here —
        // phones have no pane system (REQ-024).
        browserMode === 'inline' ? (
          sessionId ? (
            // REQ-037 — the sidebar/mobile Inspector is a scrolling column with
            // no bounded height, so BrowserPane's flex-1 collapses and the page
            // renders cut off. This shell gives it a viewport-relative height.
            <div className="flex h-[60vh] min-h-[320px] flex-col overflow-hidden">
              <LazyBrowserPane key={sessionId} sessionId={sessionId} />
            </div>
          ) : (
            <div className="rounded border border-dashed p-3 text-xs text-muted-foreground">
              Open a session to use the browser.
            </div>
          )
        ) : (
          <BrowserPaneRedirect />
        )
      ) : sessionId && ws ? (
        // REQ-162 — same shell the browser got (REQ-037): the sidebar is an
        // unbounded scrolling column, so the emulator needs a real box or its
        // viewport collapses to zero height.
        <div className="flex h-[60vh] min-h-[320px] flex-col overflow-hidden">
          <LazyTerminalPane key={sessionId} sessionId={sessionId} ws={ws} />
        </div>
      ) : (
        <div className="rounded border border-dashed p-3 text-xs text-muted-foreground">
          Open a session to use the terminal.
        </div>
      )}
      </React.Suspense>
    </div>
  );
}

/**
 * REQ-109 — desktop sidebars never render the browser surface. This card
 * is the only thing a sidebar 'browser' state can show: no iframe, no
 * session binding, just a one-click path to the tiling pane tab (same
 * upsert + focus the rails and agent actions use). Lucide icon only.
 */
function BrowserPaneRedirect() {
  const setTiling = usePaneStore((s) => s.setTiling);
  const requestBrowserTab = usePaneStore((s) => s.requestInspectorTab);
  const open = () => {
    setTiling(true);
    requestBrowserTab('browser');
  };
  return (
    <div className="flex flex-col items-center gap-2 rounded border border-dashed p-6 text-center">
      <Globe className="h-5 w-5 text-muted-foreground" />
      <p className="text-xs text-muted-foreground">The browser now opens as a pane tab, not in the sidebar.</p>
      <Button variant="default" size="sm" className="h-7 text-xs" onClick={open}>
        Open browser pane
      </Button>
    </div>
  );
}
